import React, { useState, useEffect, useRef } from "react";

const DatePicker = ({ value, onChange, placeholder = "mm/dd/yyyy", className = "", readOnly = false, max = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(value ? new Date(value) : new Date());
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update currentMonth if value changes from outside
  useEffect(() => {
    if (value) {
      setCurrentMonth(new Date(value));
    }
  }, [value]);

  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();

  const handlePrevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const handleNextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));

  const handleDateClick = (day) => {
    if (readOnly) return;
    const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    const newValue = `${currentMonth.getFullYear()}-${month}-${d}`;
    
    if (max && newValue > max) return;

    if (onChange) {
      onChange({ target: { value: newValue, name: containerRef.current.dataset.name } });
    }
    setIsOpen(false);
  };

  const getFormattedValue = (val) => {
    if (!val) return "";
    const [y, m, d] = val.split("-");
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const formattedValue = getFormattedValue(value);

  return (
    <div className={`relative inline-block ${className}`} ref={containerRef} data-name={className.includes('name=') ? className.split('name=')[1] : ''}>
      <div
        className={`flex items-center justify-between w-full ${readOnly ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'}`}
        onClick={() => !readOnly && setIsOpen(!isOpen)}
      >
        <span className={value ? "text-[#191C1D] text-[13px] font-semibold truncate" : "text-[#7D747F99] text-[13px] truncate"}>
          {formattedValue || placeholder}
        </span>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-[#4C444E] ml-2 shrink-0">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" strokeLinecap="round" />
        </svg>
      </div>

      {isOpen && (
        <div className="absolute top-full mt-2 p-4 bg-white rounded-[16px] shadow-[0px_10px_30px_#0000001A] border border-[#CEC3D04D] w-[260px] z-[9999] right-0 sm:left-0 sm:right-auto">
          <div className="flex justify-between items-center mb-4">
            <button onClick={handlePrevMonth} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F3F4F5] text-[#34094D] transition">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <div className="text-[15px] font-bold text-[#34094D]">
              {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
            </div>
            <button onClick={handleNextMonth} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F3F4F5] text-[#34094D] transition">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center mb-2">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
              <div key={day} className="text-[11px] font-semibold text-[#7D747F] uppercase">{day}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDayOfMonth }).map((_, i) => <div key={`empty-${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const isSelected = value === dateStr;
              
              // Handle timezone issues by using current local time for "today"
              const todayDate = new Date();
              const todayStr = `${todayDate.getFullYear()}-${String(todayDate.getMonth() + 1).padStart(2, '0')}-${String(todayDate.getDate()).padStart(2, '0')}`;
              const isToday = todayStr === dateStr;
              const isAfterMax = max && dateStr > max;

              return (
                <button
                  key={day}
                  onClick={() => !isAfterMax && handleDateClick(day)}
                  disabled={isAfterMax}
                  className={`w-7 h-7 mx-auto flex items-center justify-center rounded-full text-[13px] transition-colors ${
                    isSelected
                      ? "bg-[#4B2364] text-white shadow-md font-bold"
                      : isToday
                      ? "bg-[#FAF5FF] text-[#4B2364] font-bold border border-[#4B2364]"
                      : isAfterMax
                      ? "text-[#D1D5DB] cursor-not-allowed"
                      : "text-[#191C1D] hover:bg-[#F3F4F5] border border-transparent font-medium"
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
